// The AWS PPA deal — single source of truth for the notification → preliminary
// report → intake demo. Figures mirror the v1 deal analysis ("analysis v1.html"
// at the repo root): a $173M, 36-month AWS Enterprise Private Pricing Addendum
// for Demo Customer.
//
// Read by the intake demo page (src/components/intake/demo) and the Slack
// posting script (demos/slack-notifier). The budget demo is deliberately not
// seeded with this deal — a $57M/yr cloud line would dwarf that budget's scale.
//
// Keep this module dependency-free — the Slack script imports it from outside
// the React app via tsx.

export interface DemoPpaDeal {
    vendor: string;
    dealName: string;
    buyer: string;
    /** Total committed spend over the term. */
    tcv: number;
    termMonths: number;
    termStart: string;
    termEnd: string;
    /** Annual minimum commitments — the ~14% YoY ramp is the story. */
    yearlyCommit: [number, number, number];
    rampPctLabel: string;
    marketRampLabel: string;
    /** Headline discount terms from the proposed PPA. */
    crossServiceDiscountPct: number;
    ec2SpDiscountPct: number;
    ec2SpMarketLabel: string;
    computeSpDiscountPct: number;
    computeSpMarketLabel: string;
    /** Conditional credits (outside committed spend). */
    creditsTotal: number;
    creditsAiMl: number;
    creditsMigration: number;
    marketplaceCapPct: number;
    paymentTermsLabel: string;
    /** "What's typically on the table" — the preliminary band projected from
     * benchmark data alone; the full analysis narrows it into levers. */
    prelimLow: number;
    prelimHigh: number;
    prelimPctLabel: string;
    /** Top of the range bar's $ scale on the preliminary report. */
    prelimScaleMax: number;
    /** The staged full-playbook follow-up quotes these. */
    playbookTarget: number;
    playbookTargetPctLabel: string;
    playbookLevers: number;
    playbookMustHaves: number;
    /** AWS's proposal pricing expiry — their Q3 close is the clock. */
    offerExpiresIso: string;
    offerExpiresLabel: string;
    /** Package state. */
    analysesRemaining: number;
    analysesTotal: number;
    /** The delivered full negotiation playbook, in the production demo env. */
    playbookUrl: string;
}

export const DEMO_PPA: DemoPpaDeal = {
    vendor: "AWS",
    dealName: "Enterprise Private Pricing Addendum",
    buyer: "Demo Customer",
    tcv: 173_000_000,
    termMonths: 36,
    termStart: "Jan 2026",
    termEnd: "Dec 2028",
    yearlyCommit: [50_000_000, 58_000_000, 65_000_000],
    rampPctLabel: "~14% YoY",
    marketRampLabel: "8–10% YoY",
    crossServiceDiscountPct: 19,
    ec2SpDiscountPct: 25,
    ec2SpMarketLabel: "25–40%",
    computeSpDiscountPct: 22,
    computeSpMarketLabel: "25–45%",
    creditsTotal: 7_000_000,
    creditsAiMl: 4_000_000,
    creditsMigration: 3_000_000,
    marketplaceCapPct: 25,
    paymentTermsLabel: "Net 30",
    prelimLow: 10_000_000,
    prelimHigh: 25_000_000,
    prelimPctLabel: "6–14% of TCV",
    prelimScaleMax: 30_000_000,
    playbookTarget: 17_930_000,
    playbookTargetPctLabel: "10.4% of TCV",
    playbookLevers: 3,
    playbookMustHaves: 6,
    offerExpiresIso: "2026-09-30",
    offerExpiresLabel: "Sep 30",
    analysesRemaining: 14,
    analysesTotal: 20,
    playbookUrl: "https://oasis.infinityloop.ai/project/1055/analysis/1",
};

/** Whole days from today until the proposal pricing expires (never negative). */
export function daysToOfferExpiry(deal: DemoPpaDeal = DEMO_PPA): number {
    const deadline = new Date(`${deal.offerExpiresIso}T00:00:00`);
    const days = Math.ceil((deadline.getTime() - Date.now()) / 86_400_000);
    return Math.max(0, days);
}

// ---------------------------------------------------------------------------
// Formatting — shared so the Slack copy and the page always print figures the
// same way.
// ---------------------------------------------------------------------------

/** "$173M", "$13.39M" — millions, trailing zeros trimmed. */
export const fmtM = (n: number): string => {
    const m = n / 1_000_000;
    return `$${Number.isInteger(m) ? m : Number(m.toFixed(2))}M`;
};
